import React, { ChangeEvent, useCallback } from 'react';

import { TextField as RadixTextField } from '@radix-ui/themes';
import classNames from 'classnames';

import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import { renderIcon } from '../utils';
import styles from './TextField.module.less';
import {
  TextFieldProps,
  sizeToClassName,
  sizeToFontSize,
} from './TextField.types';

function TextField({
  label,
  error,
  errorMessage,
  required,
  size = '2',

  iconStart,
  iconEnd,
  iconStartSize = 16,
  iconEndSize = 16,

  tooltip,
  withTooltip = true,
  parse,

  className,
  onChange,
  ref,
  ...props
}: TextFieldProps & { ref?: React.Ref<HTMLInputElement> }) {
  const hasError = !!error || !!errorMessage;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const input = e.target as HTMLInputElement;

      // 1. Save the current cursor start and end positions
      const start = input.selectionStart;
      const end = input.selectionEnd;

      // 2. Perform the input parsing
      const parsedValue = parse ? parse(input.value) : input.value;
      e.target.value = parsedValue;
      onChange?.(e);
      input.value = parsedValue;

      // 3. Put the cursor back where it belongs
      if (typeof start === 'number' && typeof end === 'number') {
        input.setSelectionRange(start, end);
      }
    },
    [parse, onChange]
  );

  const textField = (
    <RadixTextField.Root
      {...props}
      ref={ref}
      size={size}
      className={classNames(
        className,
        sizeToFontSize[size],
        sizeToClassName[size],
        styles.textField,
        hasError && styles.error
      )}
      onChange={handleChange}
    >
      {iconStart && (
        <RadixTextField.Slot side='left'>
          {renderIcon(iconStart, iconStartSize)}
        </RadixTextField.Slot>
      )}
      {iconEnd && (
        <RadixTextField.Slot side='right'>
          {renderIcon(iconEnd, iconEndSize)}
        </RadixTextField.Slot>
      )}
    </RadixTextField.Root>
  );

  const content = withTooltip ? (
    <Tooltip open={tooltip ? undefined : false} side='bottom' content={tooltip}>
      {textField}
    </Tooltip>
  ) : (
    textField
  );

  return label ? (
    <label
      className={classNames(
        sizeToFontSize[size],
        'flex w-full flex-col gap-fly-4'
      )}
    >
      <Text size='2' highContrast weight='bold'>
        {label}
        {required && '*'}
      </Text>
      {content}
      {!!errorMessage && (
        <Text size='2' color='red'>
          {errorMessage}
        </Text>
      )}
    </label>
  ) : (
    content
  );
}

export { TextField };
